/**
 * Draft preview helper for the Business Dashboard.
 *
 * Stores the merged (saved business + unsaved editForm) snapshot in sessionStorage
 * so that BusinessDetail can render it as a "Preview — not saved" view.
 *
 * No server calls, no versioning, no persistence — the entry is cleared when the
 * user closes the tab (sessionStorage scope).
 */

const DRAFT_KEY = (id) => `f2d_draft_preview_${id}`;

export const saveDraftPreview = (business, editForm) => {
  if (!business || !business.id) return null;
  const merged = { ...business, ...(editForm || {}) };
  try {
    sessionStorage.setItem(DRAFT_KEY(business.id), JSON.stringify(merged));
  } catch (e) {
    // Storage may be full or disabled — preview will just fall back to saved copy.
    console.warn('Draft preview storage failed:', e);
  }
  return merged;
};

export const readDraftPreview = (id) => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY(id));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const clearDraftPreview = (id) => {
  try { sessionStorage.removeItem(DRAFT_KEY(id)); } catch (e) { /* ignore */ }
};

export const openDraftPreview = (business, editForm) => {
  saveDraftPreview(business, editForm);
  const url = `/business/${business.id}?preview=true&draft=1`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Signup-time preview: there is no saved business yet, so we synthesise a draft from
 * the wizard's formData and stash it under a fixed id (`__signup__`) that BusinessDetail
 * renders purely from sessionStorage without any API call.
 */
const SIGNUP_PREVIEW_ID = '__signup__';

export const openSignupPreview = (formData, planId) => {
  // Build a minimal business-shaped draft from the wizard state.
  const draft = {
    id: SIGNUP_PREVIEW_ID,
    businessName: formData.businessName || 'Your Business',
    description: formData.description || '',
    category: formData.customCategory || formData.category || '',
    address: formData.address || '',
    city: formData.city || '',
    phone: formData.phone || '',
    email: formData.businessEmail || formData.email || '',
    website: formData.website || '',
    openingHours: formData.openingHours || '',
    photos: Array.isArray(formData.photos) ? formData.photos : [],
    groupSizes: formData.groupSizes || [],
    dietaryOptions: formData.dietaryOptions || [],
    eventTypes: formData.eventTypes || [],
    venueType: formData.venueType || [],
    additionalFeatures: formData.additionalFeatures || [],
    accessibilityFeatures: formData.accessibilityFeatures || [],
    averageRating: 0,
    totalReviews: 0,
    subscriptionPlan: planId || null,
    subscriptionStatus: 'preview',
    approvalStatus: 'approved',
    isVisible: true,
  };
  saveDraftPreview(draft, {});
  window.open(`/business/${SIGNUP_PREVIEW_ID}?preview=true&draft=1`, '_blank', 'noopener,noreferrer');
};
